import styled from 'styled-components';
import Satellite from '../../../assets/images/pluvaAutoLanding/point_emoji_satellite.png';
import Tractor from '../../../assets/images/pluvaAutoLanding/point_emoji_tractor.png';
import Gear from '../../../assets/images/pluvaAutoLanding/point_emoji_gear.png';
import LevelSlider from '../../../assets/images/pluvaAutoLanding/point_emoji_level-slider.png';
import MoneyWithWings from '../../../assets/images/pluvaAutoLanding/point_emoji_money-with-wings.png';
import Flag from '../../../assets/images/pluvaAutoLanding/point_emoji_flag.png';
import Globe from '../../../assets/images/pluvaAutoLanding/point_emoiji_globe-showing.png';
import Map from '../../../assets/images/pluvaAutoLanding/point_emoji_map.png';
import { PointBadge } from './PointBadge';

interface IKeyFeature {
  icon: string;
  content: string;
  helperText: string;
  color: string;
}

const keyFeatures: IKeyFeature[] = [
  {
    icon: Satellite,
    content: '유료 RTK-GPS 사용',
    helperText: '끊김 없는 초정밀성으로 차별화',
    color: '#D6F4E5',
  },
  {
    icon: Tractor,
    content: '다양한 농기계에 설치 가능',
    helperText: '트랙터, 이앙기, 관리기 등 핸들형 농기계',
    color: '#F2F2F7',
  },
  {
    icon: Gear,
    content: '작업을 위한 다양한 기능',
    helperText: '선회(자동, 반자동) 및 직진과 후진 기능',
    color: '#E5E9FF',
  },
  {
    icon: Map,
    content: '위성 지도를 활용한 섬세한 제어',
    helperText: '물논, 야간 작업에도 빈틈없이',
    color: '#F2F2F7',
  },
  {
    icon: LevelSlider,
    content: '저속과 고속 모드 지원',
    helperText: '트랙터는 0.77~13km, 이앙기는 0.77~6km의 폭넓은 속도 지원',
    color: '#FFDBE0',
  },
  {
    icon: MoneyWithWings,
    content: '추가 트랙터 설치는 반값',
    helperText: '메인 컨트롤러 탈부착으로 경제적 사용',
    color: '#F2F2F7',
  },
  {
    icon: Flag,
    content: '국내 제조로 높은 신뢰성 확보',
    helperText: '국내 연구, 생산 및 핵심 부품 국산화',
    color: '#FFEACC',
  },
  {
    icon: Globe,
    content: '전국 각지 대리점망 구축 완료',
    helperText: '자사 전문 매니저의 A/S 지원',
    color: '#D6F4E5',
  },
];

const KeyFeatureCard = ({ feature }: { feature: IKeyFeature }) => {
  return (
    <Card>
      <PointIconWrapper bgColor={feature.color}>
        <PointIcon src={feature.icon} alt={`${feature.content}의 아이콘`} />
      </PointIconWrapper>
      <CardContentWrapper>
        <CardContent>{feature.content}</CardContent>
        <CardHelperText>{feature.helperText}</CardHelperText>
      </CardContentWrapper>
    </Card>
  );
};

export const KeyFeaturesSection = () => {
  return (
    <Container>
      <TitleSection>
        <PointBadge title={'빠르게 살펴보기'} />
        <Title>{'플루바 오토 자율주행,\n한눈에 보는 핵심 포인트!'}</Title>
      </TitleSection>
      <KeyFeatureGroup>
        {keyFeatures.map((feature: IKeyFeature) => {
          return <KeyFeatureCard feature={feature} key={feature.content} />;
        })}
      </KeyFeatureGroup>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  padding: 48px 24px;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;

  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/XXX-Large/xxxl-bold */
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 31.2px */
  white-space: pre-line;
`;

const KeyFeatureGroup = styled.div`
  display: flex;
  padding: 0px var(--radius-large, 16px);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;

  @media screen and (max-width: 340px) {
    padding: 0;
  }
`;

const Card = styled.div`
  display: flex;
  padding: 24px 0px;
  align-items: center;
  gap: 16px;
  align-self: stretch;

  border-bottom: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);

  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
    border: none;
  }
`;

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  flex: 1 0 0;
`;

const CardContent = styled.p`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-medium */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */
`;

const CardHelperText = styled.p`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */
`;

const PointIconWrapper = styled.div<{ bgColor: string }>`
  display: flex;
  width: 48px;
  min-width: 48px;
  height: 48px;
  padding: var(--radius-medium, 8px);
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-medium, 8px);
  background: ${(prop) => prop.bgColor};
`;

const PointIcon = styled.img`
  width: 24px;
  height: 24px;
`;
